// src/app/modules/Dashboard/hooks/useOrgConfig.js
// 🎯 Custom hook for accessing organization-specific configuration

import { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { 
  getOrgConfig, 
  getOrgLabel, 
  hasOrgFeature, 
  showOrgModule,
  getOrgSidebarConfig 
} from '../config/organizationTypes';

/**
 * Custom hook to get organization-specific configuration
 * @returns {Object} Organization configuration and helper functions
 */
export function useOrgConfig() {
  const org = useSelector((state) => state.auth.organization, shallowEqual);
  
  const orgType = org?.type || 'default';
  
  const config = useMemo(() => getOrgConfig(orgType), [orgType]);
  
  // Helper function to get labels
  const getLabel = (labelKey) => getOrgLabel(orgType, labelKey);
  
  // Helper function to check features
  const hasFeature = (featureKey) => hasOrgFeature(orgType, featureKey);
  
  // Helper function to check modules
  const showModule = (moduleKey) => showOrgModule(orgType, moduleKey);
  
  // Get sidebar config
  const sidebarConfig = useMemo(() => getOrgSidebarConfig(orgType), [orgType]);
  

  const isResidentMode = useMemo(() => {
    return [
      'residential_estate', 
      'residential_building', 
      'hospitality'
    ].includes(orgType);
  }, [orgType]);

  // Business Mode: Places where people work or visit for commerce
  const isBusinessMode = useMemo(() => {
    return [
      'office_building', 
      'commercial_building', 
      'retail', 
      'educational', 
      'healthcare', 
      'event_venue'
    ].includes(orgType);
  }, [orgType]);


  return {
    org,
    orgType,
    config,
    labels: config.labels,
    features: config.features,
    modules: config.modules,
    isResidentMode, // 🏠 True for Estates/Apartments
    isBusinessMode,
    sidebarConfig,
    getLabel,
    hasFeature,
    showModule,
  };
}

// Usage example:
/*
const MyComponent = () => {
  const { getLabel, hasFeature, showModule } = useOrgConfig();
  
  return (
    <div>
      <h1>{getLabel('dashboard')}</h1>
      {hasFeature('hasResidents') && <ResidentsSection />}
      {showModule('showVehicleTracking') && <VehicleTracker />}
    </div>
  );
};
*/